import { useMemo } from 'react';
import useContentRefs from '@hooks/useContentRefs';

export const getWhatDetailContentRef = whatDetail => whatDetail?.content || whatDetail?.contentRef;

export const getRuleContentRef = rule => getWhatDetailContentRef(rule?.whatDetail || rule);

export const getContentTitle = content =>
  content?.title ||
  content?.json?.title ||
  content?.json?.name ||
  content?.name ||
  content?.latestVersion?.name ||
  content?.latest_version?.name;

export const getContentAudienceType = content => content?.audienceType || content?.audience_type;

export const getContentType = content =>
  content?.type || content?.metadata?.type || content?.content_type?.split('_')?.[1];

export const getContentId = content =>
  content?.uuid || content?.id || content?.product_id || content?.content_id || content?.content;

export const hydrateWhatDetail = (whatDetail, content) => {
  if (!whatDetail && !content) return whatDetail;

  const contentRef = getWhatDetailContentRef(whatDetail) || content?.content;
  return {
    ...(whatDetail || {}),
    ...(content || {}),
    ...(contentRef ? { content: contentRef, contentRef } : {}),
  };
};

export const hydrateRule = (rule, contentMap) => {
  if (!rule?.whatDetail) return rule;

  const contentRef = getRuleContentRef(rule);
  return {
    ...rule,
    whatDetail: hydrateWhatDetail(rule.whatDetail, contentRef ? contentMap[contentRef] : undefined),
  };
};

const uniqueRefs = refs =>
  refs.reduce((acc, ref) => {
    if (ref && !acc.includes(ref)) acc.push(ref);
    return acc;
  }, []);

export const useRuleContentRefs = (rules = []) => {
  const validRules = useMemo(() => rules.filter(Boolean), [rules]);
  const refs = useMemo(() => uniqueRefs(validRules.map(getRuleContentRef)), [validRules]);
  const contentMap = useContentRefs(refs);

  const hydratedRules = useMemo(() => validRules.map(rule => hydrateRule(rule, contentMap)), [
    validRules,
    contentMap,
  ]);

  return {
    refs,
    contentMap,
    rules: hydratedRules,
    hydrateWhatDetail: whatDetail => {
      const contentRef = getWhatDetailContentRef(whatDetail);
      return hydrateWhatDetail(whatDetail, contentRef ? contentMap[contentRef] : undefined);
    },
  };
};

export default useRuleContentRefs;
